/**
 * Suites proposées par la confirmation de réservation (docs/ux-v2/SPEC.md CO4, CO5, CO6 ; demandes de Luc du 08/10 et
 * de Daphné du 08/10) : « Réserver le même cours la semaine prochaine » (s'il est ouvert), « Réserver un autre cours
 * avec {prénom} » et « Réserver un autre cours au studio {studio} ».
 * Fonctions pures : la fenêtre de réservation (utils/functions.ts isClassBookable) et les cours du client sont passés
 * en paramètre, les cours viennent de `class/planning` (api/planning.ts, cache partagé).
 *
 * Mêmes règles que les cartes du planning (lib/planning/status.ts classStatus) : le libellé de la confirmation ne
 * contredit jamais l'étiquette de la carte du même cours (« Réserver », « Liste d'attente », « Réservé »…).
 */
import { addDays, addWeeks, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { PlanningClass } from '../../../api/planning';
import { shortStudioName } from '../../../lib/planning/brands';
import { eventStudioName, studioIdOf, type EventSplit } from '../../../lib/planning/eventStudios';
import { HIDDEN_STUDIOS, siteKey } from '../../../lib/planning/filters';
import { classStatus, isEnrollment, type ClassState } from '../../../lib/planning/status';
import { isoWeekOf, weekStartOf } from '../../../lib/planning/week';

const NBSP = ' ';

/** Cours réservé, tel que le lit la confirmation (`GET class/{id}`) : champs utiles seulement. */
export interface BookedClassRef {
  Id: number;
  ClassScheduleId?: number | null;
  StartDateTime: string;
  /** Fin du cours : le prochain cours du studio commence après (CO6). */
  EndDateTime?: string | null;
  IsCourse?: boolean | null;
  ClassDescription?: {
    Id?: number | null;
    Name?: string | null;
    Program?: { Id?: number | null; ScheduleType?: string | null } | null;
  } | null;
  Location?: { Id?: number | null; SiteID?: number | string | null; Name?: string | null } | null;
  Staff?: { Id?: number | null; FirstName?: string | null; DisplayName?: string | null } | null;
}

/* ------------------------------------------------------------------------------------------------ dates */

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * « 2026-10-09T07:45:00 » + 7 jours → « 2026-10-16T07:45:00 ». Calcul sur la date écrite (heure murale du site, que
 * Mindbody envoie sans fuseau) : même heure de part et d'autre d'un changement d'heure, quel que soit le fuseau du
 * navigateur. null si la date est illisible.
 */
export function shiftLocalIso(iso: string | null | undefined, days: number): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})(T[\d:.]+)?/.exec(iso ?? '');
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}${m[4] ?? ''}`;
}

const dayOf = (iso: string) => iso.slice(0, 10);
const minuteOf = (iso: string) => iso.slice(0, 16);
const minutesOfDay = (iso: string) => {
  const m = /T(\d{2}):(\d{2})/.exec(iso);
  return m ? Number(m[1]) * 60 + Number(m[2]) : 0;
};

const capitalize = (text: string) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

/** « Vendredi 16 oct. · 07:45 » : jour, date courte et heure, insécables entre eux (le « · » ne commence pas de ligne). */
export function shortClassDate(start: Date): string {
  return `${capitalize(format(start, `EEEE d${NBSP}MMM`, { locale: fr }))}${NBSP}· ${format(start, 'HH:mm')}`;
}

/**
 * « Prochain cours : lundi 12 oct. · 18:30 » (lignes du coach et du studio). Date et heure insécables : à l'étroit
 * (360 px), la ligne se coupe après « Prochain cours : », jamais entre « oct. · » et l'heure (« 18:00 » seul sur sa
 * ligne).
 */
export function nextClassDetail(startDateTime: string): string {
  return `Prochain cours${NBSP}: ${shortClassDate(new Date(startDateTime))
    .replace(/^./, (l) => l.toLowerCase())
    .replace(/ /g, NBSP)}`;
}

/**
 * Détail le plus LARGE possible des lignes du coach et du studio, pour la place réservée (components/FollowUps) :
 * « Prochain cours : dimanche 27 sept. · 20:00 ». Chiffres tabulaires (v2) : seuls comptent le jour, le nombre de
 * chiffres de la date et le mois. Mesuré dans la police de la v2 (Helvetica Neue LT de Dynamo, Chromium et WebKit,
 * relecture du 09/10) : 258,72 px, le plus large des 168 détails possibles (7 jours × 12 mois × date à 1 ou 2
 * chiffres ; « vendredi 16 oct. », l'ancien exemple : 244,45 px, plus étroit que 36 d'entre eux). Dans d'autres polices,
 * « dimanche 27 mars » peut être plus large de 1,7 px au plus (Georgia) : components/FollowUps retient alors la dernière
 * ligne plutôt que de laisser le bloc grandir à l'écran.
 */
export const WIDEST_NEXT_CLASS_DETAIL = nextClassDetail('2026-09-27T20:00:00');

/* ---------------------------------------------------------------------------------------- semaines lues */

export interface WeekRef {
  week: number;
  year: number;
}

/** Semaines d'avant celle du cours lues au plus (fenêtre de réservation de 15 jours : 2 au plus en pratique). */
export const MAX_EARLIER_WEEKS = 3;

/**
 * Semaines ISO du planning à lire : celle du cours, la suivante (le même cours la semaine prochaine), puis celles qui
 * vont de la semaine courante à celle du cours (le prochain cours du coach peut tomber avant le cours réservé : sans
 * elles, « Prochain cours : lundi 19 oct. » pour un coach qui en a un demain). null si la date du cours est illisible.
 */
export function followUpWeeks(startDateTime: string | null | undefined, now: Date): WeekRef[] | null {
  const start = new Date(startDateTime ?? '');
  if (Number.isNaN(start.getTime())) return null;
  const weeks = [isoWeekOf(start), isoWeekOf(addDays(start, 7))];
  const classWeekStart = weekStartOf(start).getTime();
  for (let cursor = weekStartOf(now); cursor.getTime() < classWeekStart; cursor = addWeeks(cursor, 1)) {
    if (weeks.length >= 2 + MAX_EARLIER_WEEKS) break;
    weeks.push(isoWeekOf(cursor));
  }
  return weeks;
}

/* ------------------------------------------------------------------------------- même cours, semaine +1 */

const normalizeName = (name: string | null | undefined) =>
  (name ?? '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('fr');

/**
 * Le cours `c` est-il le même cours que le cours réservé ? Même nom (aux espaces et à la casse près) ou même
 * description Mindbody (`ClassDescription.Id`). Un nom qui diffère en quoi que ce soit (« DYNAMO SIGNATURE » /
 * « DYNAMO SIGNATURE* », « … + en option RUN CLUB ») n'est pas le même cours : rien plutôt qu'un faux ami.
 */
export function isSameCourse(booked: BookedClassRef, c: PlanningClass): boolean {
  const name = normalizeName(booked.ClassDescription?.Name);
  if (name && normalizeName(c.ClassDescription?.Name) === name) return true;
  const descriptionId = Number(booked.ClassDescription?.Id);
  return Number.isFinite(descriptionId) && descriptionId > 0 && Number(c.ClassDescription?.Id) === descriptionId;
}

/**
 * Le même cours la semaine suivante, sur le site du cours réservé, parmi les cours de `class/planning` :
 * 1. même `ClassScheduleId` (une série Mindbody = un jour de la semaine et une heure), le même jour à + 7 jours, ET le
 *    même cours (isSameCourse) : une série ne garde pas toujours son cours d'une semaine à l'autre (revue du 09/10,
 *    données réelles des semaines 41 à 45 : 30 couples, dont 86149 DYNAMO SIGNATURE → 86150 DYNAMO STRONG, 39102
 *    RIISE PILATES → 39103 RIISE YOGA, 82848 DYNAMO SIGNATURE* → 82849 Old school only) ; s'il y en a plusieurs, le
 *    plus proche de l'heure du cours réservé (séance déplacée) ;
 * 2. sinon même nom de cours, même studio, même jour et même heure à + 7 jours (série recréée dans Mindbody : 9 cours
 *    RIISE sur 352 en semaine 41, relevé du 08/10 ; ou cours passé sur une autre série du même créneau).
 * Sinon null : le CTA est masqué.
 */
export function findSameClassNextWeek(
  booked: BookedClassRef,
  classes: ReadonlyArray<PlanningClass>,
  siteId: string,
): PlanningClass | null {
  const target = shiftLocalIso(booked.StartDateTime, 7);
  if (!target) return null;
  const candidates = classes.filter(
    (c) => String(c.SiteId) === siteId && c.Id !== booked.Id && !!c.StartDateTime && isSameCourse(booked, c),
  );

  const scheduleId = Number(booked.ClassScheduleId);
  if (Number.isFinite(scheduleId) && scheduleId > 0) {
    const targetMinutes = minutesOfDay(target);
    const sameSeries = candidates
      .filter((c) => Number(c.ClassScheduleId) === scheduleId && dayOf(c.StartDateTime) === dayOf(target))
      .sort(
        (a, b) =>
          Math.abs(minutesOfDay(a.StartDateTime) - targetMinutes) - Math.abs(minutesOfDay(b.StartDateTime) - targetMinutes),
      );
    if (sameSeries[0]) return sameSeries[0];
  }

  const name = normalizeName(booked.ClassDescription?.Name);
  const locationId = booked.Location?.Id;
  if (!name || locationId === null || locationId === undefined) return null;
  return (
    candidates.find(
      (c) =>
        minuteOf(c.StartDateTime) === minuteOf(target) &&
        Number(c.Location?.Id) === Number(locationId) &&
        normalizeName(c.ClassDescription?.Name) === name,
    ) ?? null
  );
}

/* ---------------------------------------------------------------------------------------------- coach */

/**
 * Coach générique de Mindbody (« Coach Remplaçant » sur Dynamo, RIISE et Punch Paris, relevé du 08/10 ; variantes
 * prévues) : pas de planning « à son nom » à proposer.
 */
export function isGenericCoachName(name: string | null | undefined): boolean {
  const n = (name ?? '').trim().toLocaleLowerCase('fr');
  if (!n) return true;
  return /rempla[cç]ant|substitut|^coach$|^(?:tba|tbd|n\/a|staff|à définir|a definir|inconnu)$/.test(n);
}

/**
 * Prénom du coach pour « … avec {prénom} » (mêmes noms que les cartes du planning, `Staff.FirstName`, sinon
 * DisplayName) : espaces resserrées ; tout en capitales ou tout en minuscules → capitale initiale à chaque partie
 * (« JEAN-MARC » → « Jean-Marc ») ; initiale de nom ajoutée par le studio pour distinguer deux homonymes ponctuée
 * (« Alice-G », « Cynthia H » → « Alice G. », « Cynthia H. ») ; duo (« Justine & Elisa ») → « Justine et Elisa ».
 * Espaces insécables : le prénom ne se sépare pas de son initiale ni du « et ».
 */
export function coachFirstName(staff: BookedClassRef['Staff']): string {
  let name = (staff?.FirstName || staff?.DisplayName || '').trim().replace(/\s+/g, ' ');
  if (!name) return '';
  if (name === name.toUpperCase() || name === name.toLowerCase()) {
    name = name.toLowerCase().replace(/(^|[\s'’-])(\p{L})/gu, (_, sep: string, letter: string) => sep + letter.toUpperCase());
  }
  name = name
    .split(/\s*&\s*/)
    .map((part) => part.replace(/^(.*\p{L})[\s-]+(\p{Lu})\.?$/u, `$1${NBSP}$2.`))
    .join(`${NBSP}et `);
  return name;
}

/* ------------------------------------------------------------------------------------- offres */

export interface FollowUpContext {
  now: Date;
  /** Site Mindbody du cours réservé (= site courant : la réservation se fait toujours sur le site de la marque). */
  siteId: string;
  /** = utils/functions.ts isClassBookable (fenêtre de réservation `days` du site, cours à venir). */
  isBookable: (c: PlanningClass) => boolean;
  /** Le client a déjà une visite (non annulée) sur ce cours. */
  isMine: (c: PlanningClass) => boolean;
  /** Réglage `days` du site. */
  numberOfDays: number;
  brandName?: string;
  /**
   * Noms des marques retirés en tête du nom d'un studio (« dynamo Boétie » → « Boétie »), comme le planning
   * (lib/planning/brands.ts shortStudioName : marques de `uxV2.brands` et réglage « Nom de la marque »). Par défaut :
   * `brandName`.
   */
  brandNames?: ReadonlyArray<string>;
  /**
   * Page coach (shortcode `[mindbody coach]`, fiches coach du thème) : le planning de l'app n'y montre que ce coach
   * (screens/Planning lockedCoachKey, Id dans le site courant). Un lien vers le planning d'un studio ou d'un autre coach
   * y mènerait aux seuls cours de ce coach, sans le cours annoncé : ces lignes ne sont pas proposées (relecture du
   * 09/10). null ou absent : page de réservation.
   */
  lockedCoachId?: number | null;
  /**
   * Studios virtuels du studio Monday Event de Paris (lib/planning/eventStudios.ts, réglage du site) : la ligne du
   * studio d'un Run Club propose l'entrée « Run Club » (`#/studio/10-46`), celle d'un événement l'entrée Special Events.
   * null ou absent : studio Mindbody.
   */
  eventSplit?: EventSplit | null;
}

/** Studio du cours dans la route du planning (`#/studio/{id}`) : Location.Id, ou studio virtuel (« 10-46 »). */
const routeStudioOf = (c: BookedClassRef | PlanningClass, ctx: Pick<FollowUpContext, 'siteId' | 'eventSplit'>) =>
  studioIdOf({ ...c, SiteId: ctx.siteId }, ctx.eventSplit ?? null);

/** Nom du studio virtuel du cours réservé ; null s'il reste dans son studio Mindbody. */
const virtualStudioName = (booked: BookedClassRef, ctx: Pick<FollowUpContext, 'siteId' | 'eventSplit'>) =>
  eventStudioName({ ...booked, SiteId: ctx.siteId }, ctx.eventSplit ?? null);

/** Page coach : planning de l'app verrouillé sur un coach (FollowUpContext.lockedCoachId). */
const lockedCoachOf = (ctx: Pick<FollowUpContext, 'lockedCoachId'>) =>
  ctx.lockedCoachId !== null && ctx.lockedCoachId !== undefined ? Number(ctx.lockedCoachId) : null;

/** Étiquette d'un cours pour la confirmation (= classStatus du planning), null s'il n'est pas proposable. */
function stateOf(c: PlanningClass, ctx: FollowUpContext): ClassState | null {
  if (!c.Active || c.IsCanceled || isEnrollment(c) || c.IsCourse) return null;
  return classStatus(c, {
    now: ctx.now,
    isCurrentSite: true,
    isBookable: ctx.isBookable,
    isMine: ctx.isMine(c),
    brand: { name: ctx.brandName ?? '', url: null },
    numberOfDays: ctx.numberOfDays,
  }).state;
}

export type NextWeekState = 'book' | 'waitlist' | 'mine';

/**
 * Variantes « Le même cours la semaine prochaine » + étiquette « Liste d'attente » (complet, liste d'attente ouverte) ou
 * « Réservé » (déjà réservé par le client) : prêtes, mais COUPÉES tant que Luc ne les a pas validées (SPEC § 13.2). Sa
 * demande : le CTA seulement si le cours est ouvert à la réservation. Passer à `true` pour les montrer.
 */
export const NEXT_WEEK_WAITLIST_AND_BOOKED = false;

/** États de la semaine prochaine proposés par défaut : `book` seul (ouvert à la réservation), cf. ci-dessus. */
export const NEXT_WEEK_STATES: ReadonlyArray<NextWeekState> = NEXT_WEEK_WAITLIST_AND_BOOKED
  ? ['book', 'waitlist', 'mine']
  : ['book'];

export interface NextWeekOffer {
  cls: PlanningClass;
  state: NextWeekState;
  /** Fiche du cours (`#/class/{Id}`). */
  route: string;
  title: string;
  /** « Vendredi 16 oct. · 07:45 » (+ « · avec Emma » si le coach change). */
  detail: string;
  /** Étiquette d'état (liste d'attente, déjà réservé) ; null : cours ouvert, flèche seule. */
  tag: { label: string; tone: 'amber' | 'green' } | null;
}

/**
 * « Réserver le même cours la semaine prochaine », seulement s'il est OUVERT à la réservation (demande de Luc) : actif,
 * non annulé (le planning n'envoie pas les cours annulés), dans la fenêtre de réservation du site, avec des places
 * (`book`, même état que la carte du planning). Masqué sinon : complet (avec ou sans liste d'attente), déjà réservé par
 * le client, hors fenêtre (« Ouvre le … »), passé.
 * Variantes prêtes, coupées par défaut (NEXT_WEEK_STATES, à valider par Luc) : liste d'attente ouverte (`waitlist`) →
 * « Le même cours la semaine prochaine » + étiquette « Liste d'attente » (orange) ; déjà réservé (`mine`) → étiquette
 * « Réservé » (vert), vers la fiche.
 */
export function nextWeekOffer(
  booked: BookedClassRef,
  next: PlanningClass | null,
  ctx: FollowUpContext,
  states: ReadonlyArray<NextWeekState> = NEXT_WEEK_STATES,
): NextWeekOffer | null {
  if (!next) return null;
  const state = stateOf(next, ctx);
  if (state !== 'book' && state !== 'waitlist' && state !== 'mine') return null;
  if (!states.includes(state)) return null;

  const bookedCoach = booked.Staff?.Id;
  const nextCoach = coachFirstName(next.Staff);
  const coachChanged =
    next.Staff?.Id !== undefined && bookedCoach !== undefined && bookedCoach !== null && Number(next.Staff.Id) !== Number(bookedCoach);
  const withCoach = coachChanged
    ? isGenericCoachName(next.Staff?.FirstName)
      ? `${NBSP}· coach remplaçant`
      : nextCoach
        ? `${NBSP}· avec${NBSP}${nextCoach}`
        : ''
    : '';

  return {
    cls: next,
    state,
    route: `/class/${next.Id}`,
    title: state === 'book' ? 'Réserver le même cours la semaine prochaine' : 'Le même cours la semaine prochaine',
    detail: `${shortClassDate(new Date(next.StartDateTime))}${withCoach}`,
    tag:
      state === 'waitlist'
        ? { label: 'Liste d’attente', tone: 'amber' }
        : state === 'mine'
          ? { label: 'Réservé', tone: 'green' }
          : null,
  };
}

export interface CoachOffer {
  /** Id Mindbody du coach dans le site du cours (route `#/coach/{Id}`). */
  coachId: number;
  name: string;
  route: string;
  /** Premier de ses autres cours ouverts : semaine et jour sur lesquels le planning s'ouvre. */
  next: PlanningClass;
  /** Nombre de ses autres cours ouverts trouvés dans les semaines lues. */
  count: number;
  title: string;
  detail: string;
}

/**
 * « Réserver un autre cours avec {prénom} » vers le planning de ce coach. Masqué si le coach est inconnu (sans Id ni
 * prénom), générique (« Coach Remplaçant »), ou s'il n'a pas d'AUTRE cours ouvert (`book` ou `waitlist`, dans la
 * fenêtre de réservation, pas déjà réservé par le client) parmi les cours lus ; le cours de la semaine prochaine déjà
 * proposé juste au-dessus (`excludeIds`) ne compte pas : le lien mènerait au même cours. Page d'un AUTRE coach
 * (`ctx.lockedCoachId`) : masqué, le planning de la page n'y montrerait que les cours de celui de la page.
 */
export function coachOffer(
  booked: BookedClassRef,
  classes: ReadonlyArray<PlanningClass>,
  ctx: FollowUpContext,
  excludeIds: ReadonlyArray<number> = [],
): CoachOffer | null {
  const coachId = Number(booked.Staff?.Id);
  if (!Number.isFinite(coachId) || coachId <= 0) return null;
  const locked = lockedCoachOf(ctx);
  if (locked !== null && locked !== coachId) return null;
  if (isGenericCoachName(booked.Staff?.FirstName || booked.Staff?.DisplayName)) return null;
  const name = coachFirstName(booked.Staff);
  if (!name) return null;

  const seen = new Set<number>([booked.Id, ...excludeIds]);
  const others = classes
    .filter((c) => {
      if (String(c.SiteId) !== ctx.siteId || Number(c.Staff?.Id) !== coachId || seen.has(c.Id)) return false;
      seen.add(c.Id);
      const state = stateOf(c, ctx);
      return state === 'book' || state === 'waitlist';
    })
    .sort((a, b) => a.StartDateTime.localeCompare(b.StartDateTime));
  if (!others[0]) return null;

  return {
    coachId,
    name,
    route: `/coach/${coachId}`,
    next: others[0],
    count: others.length,
    title: `Réserver un autre cours avec${NBSP}${name}`,
    detail: nextClassDetail(others[0].StartDateTime),
  };
}

/* --------------------------------------------------------------------------------------------- studio */

/**
 * Studio du cours réservé, s'il peut être proposé : Id Mindbody connu (`Location.Id`, entier positif) et proposé par
 * le filtre Studios du planning (pas l'un des studios masqués, HIDDEN_STUDIOS). null sinon.
 */
export function bookedStudioId(booked: BookedClassRef, siteId: string): number | null {
  const id = Number(booked.Location?.Id);
  if (!Number.isInteger(id) || id <= 0) return null;
  return HIDDEN_STUDIOS.includes(siteKey(siteId, id)) ? null : id;
}

/**
 * Nom du studio tel que l'affichent le filtre Studios et les cartes du planning (shortStudioName : sans le nom de la
 * marque en tête, « dynamo Boétie » → « Boétie », « Punch - Louvre » → « Louvre »), espaces resserrées. '' s'il est
 * inconnu.
 */
export function studioName(locationName: string | null | undefined, brandNames: ReadonlyArray<string>): string {
  return shortStudioName((locationName ?? '').trim(), [...brandNames]).trim().replace(/\s+/g, ' ');
}

const brandNamesOf = (ctx: Pick<FollowUpContext, 'brandName' | 'brandNames'>) =>
  (ctx.brandNames?.length ? ctx.brandNames : [ctx.brandName ?? '']).map((n) => n.trim()).filter(Boolean);

/**
 * Studios du site qui ont au moins un cours visible au planning (actif, non annulé) parmi `classes`, hors studios
 * masqués du filtre Studios ; le studio du cours réservé compte toujours.
 */
export function siteStudioIds(
  classes: ReadonlyArray<PlanningClass>,
  siteId: string,
  bookedStudio: number | null = null,
): Set<number> {
  const ids = new Set<number>(bookedStudio !== null ? [bookedStudio] : []);
  classes.forEach((c) => {
    const id = Number(c.Location?.Id);
    if (String(c.SiteId) !== siteId || !c.Active || c.IsCanceled || !Number.isInteger(id) || id <= 0) return;
    if (!HIDDEN_STUDIOS.includes(siteKey(siteId, id))) ids.add(id);
  });
  return ids;
}

/**
 * Le cours `c` commence-t-il après le cours réservé ? À sa fin ou plus tard (heure murale du site, comme Mindbody
 * l'écrit) : un cours qui commence pendant le cours réservé ne peut pas être suivi. Fin inconnue : après son début.
 */
function startsAfterBooked(c: PlanningClass, booked: BookedClassRef): boolean {
  if (!c.StartDateTime) return false;
  const end = booked.EndDateTime ? minuteOf(booked.EndDateTime) : null;
  return end ? minuteOf(c.StartDateTime) >= end : minuteOf(c.StartDateTime) > minuteOf(booked.StartDateTime);
}

/**
 * Libellé de la ligne du studio : « Réserver un autre cours au studio {studio} », « au », « studio » et le premier mot
 * du nom insécables (« au » ne finit jamais une ligne, « studio » ne s'y sépare pas du nom). Texte simple, équilibré
 * comme les deux autres lignes (`text-wrap: balance`) : plus de groupe en bloc en ligne (relecture du 09/10, SPEC
 * § 13.9 : Chromium équilibrait mal autour de ce bloc, « Réserver un autre / cours au studio Champs-Elysées »).
 */
export const studioTitle = (name: string): string => `Réserver un autre cours au${NBSP}studio${NBSP}${name}`;

export interface StudioOffer {
  /** Id Mindbody du studio dans le site du cours (route `#/studio/{Id}`, sauf studio virtuel : `route`). */
  studioId: number;
  name: string;
  route: string;
  /** Premier des autres cours ouverts du studio après le cours réservé : semaine et jour où le planning s'ouvre. */
  next: PlanningClass;
  /** Nombre d'autres cours ouverts du studio après le cours réservé, dans les semaines lues. */
  count: number;
  title: string;
  detail: string;
}

/**
 * « Réserver un autre cours au studio {studio} » (demande de Daphné du 08/10, SPEC CO6) vers le planning de ce studio
 * (`#/studio/{Location.Id}`, pré-filtre PL13 du planning). Mêmes règles et mêmes cours lus que la ligne du coach
 * (coachOffer) : autres cours ouverts (`book` ou `waitlist`, dans la fenêtre de réservation, pas déjà réservés par le
 * client), du site du cours ; le cours de la semaine prochaine déjà proposé (`excludeIds`) ne compte pas. Détail : le
 * premier d'entre eux qui commence APRÈS le cours réservé (à sa fin ou plus tard). Masqué si le studio est inconnu
 * (sans Id ni nom) ou masqué du filtre Studios, si le site n'a qu'un studio (le planning du studio serait le planning
 * entier), ou s'il n'y a aucun autre cours ouvert du studio après le cours réservé. Masqué aussi sur une page coach
 * (`ctx.lockedCoachId`) : le planning de la page n'y montrerait que les cours du coach.
 */
export function studioOffer(
  booked: BookedClassRef,
  classes: ReadonlyArray<PlanningClass>,
  ctx: FollowUpContext,
  excludeIds: ReadonlyArray<number> = [],
): StudioOffer | null {
  if (lockedCoachOf(ctx) !== null) return null;
  const studioId = bookedStudioId(booked, ctx.siteId);
  if (studioId === null) return null;
  const site = classes.filter((c) => String(c.SiteId) === ctx.siteId);
  if (siteStudioIds(site, ctx.siteId, studioId).size < 2) return null;
  const routeId = routeStudioOf(booked, ctx);
  const inStudio = site.filter((c) => Number(c.Location?.Id) === studioId && routeStudioOf(c, ctx) === routeId);
  const name =
    virtualStudioName(booked, ctx) ??
    studioName(booked.Location?.Name || inStudio.find((c) => c.Location?.Name)?.Location.Name, brandNamesOf(ctx));
  if (!name) return null;

  const seen = new Set<number>([booked.Id, ...excludeIds]);
  const others = inStudio
    .filter((c) => {
      if (seen.has(c.Id)) return false;
      seen.add(c.Id);
      if (!startsAfterBooked(c, booked)) return false;
      const state = stateOf(c, ctx);
      return state === 'book' || state === 'waitlist';
    })
    .sort((a, b) => a.StartDateTime.localeCompare(b.StartDateTime));
  if (!others[0]) return null;

  return {
    studioId,
    name,
    route: `/studio/${routeId}`,
    next: others[0],
    count: others.length,
    title: studioTitle(name),
    detail: nextClassDetail(others[0].StartDateTime),
  };
}

/**
 * Nom du studio pour la place réservée du bloc (components/FollowUps : une troisième ligne de la hauteur de celle du
 * studio), connu dès la confirmation, avant les semaines du planning ; null si la ligne du studio ne peut pas
 * apparaître : page coach, studio inconnu ou masqué, ou site à un seul studio d'après les cours DÉJÀ en cache
 * (`cached` : semaines du planning du site SANS filtre de coach, useFollowUps cachedPlanningClasses ; une semaine lue
 * pour la page d'un coach ne contient que ses cours). Rien en cache : on ne sait pas, la place compte la ligne.
 */
export function studioSlotName(
  booked: BookedClassRef,
  cached: ReadonlyArray<PlanningClass>,
  ctx: Pick<FollowUpContext, 'siteId' | 'brandName' | 'brandNames' | 'lockedCoachId' | 'eventSplit'>,
): string | null {
  if (lockedCoachOf(ctx) !== null) return null;
  const studioId = bookedStudioId(booked, ctx.siteId);
  if (studioId === null) return null;
  const known = cached.some((c) => String(c.SiteId) === ctx.siteId && c.Active && !c.IsCanceled);
  if (known && siteStudioIds(cached, ctx.siteId, studioId).size < 2) return null;
  return virtualStudioName(booked, ctx) ?? (studioName(booked.Location?.Name, brandNamesOf(ctx)) || null);
}

/* ------------------------------------------------------------------------------------- lignes du bloc */

export interface FollowUpOffers {
  nextWeek: NextWeekOffer | null;
  coach: CoachOffer | null;
  studio: StudioOffer | null;
}

/** Lignes affichées, dans l'ordre du bloc (« next|coach|studio », « |coach| »…) ; '' : pas de bloc. */
export const followUpRows = (offers: FollowUpOffers | null): string =>
  offers ? `${offers.nextWeek ? 'next' : ''}|${offers.coach ? 'coach' : ''}|${offers.studio ? 'studio' : ''}` : '';

const rowCount = (offers: FollowUpOffers) => [offers.nextWeek, offers.coach, offers.studio].filter(Boolean).length;

/**
 * Lignes qui tiennent dans la place réservée (`slotRows` lignes) : si les suites en ont plus (place de deux lignes, site
 * cru à un seul studio, trois suites arrivées), la ligne du studio attend : elle est ajoutée quand le bloc est sous le
 * bas de l'écran (lignes changées après coup, components/FollowUps), sans décalage visible.
 */
export function fitToSlot(offers: FollowUpOffers, slotRows: number): FollowUpOffers {
  return rowCount(offers) > slotRows && offers.studio ? { ...offers, studio: null } : offers;
}

/**
 * Les suites sans leur dernière ligne (studio, sinon coach), quand le bloc arrivé dépasse la hauteur de la place
 * réservée et qu'il ne peut pas grandir sans décalage visible (components/FollowUps) : la ligne retirée attend, comme
 * pour fitToSlot, que le bloc soit sous le bas de l'écran. null s'il ne reste qu'une ligne (gardée).
 */
export function withoutLastRow(offers: FollowUpOffers): FollowUpOffers | null {
  if (rowCount(offers) < 2) return null;
  if (offers.studio) return { ...offers, studio: null };
  return { ...offers, coach: null };
}
